import { devtoolsExchange } from '@urql/devtools';
import { authExchange, AuthUtilities } from '@urql/exchange-auth';
import { cacheExchange } from '@urql/exchange-graphcache';
import { persistedExchange } from '@urql/exchange-persisted';
import { getIntrospectedSchema } from '@urql/introspection';
import { HomeTilesV2 } from '@varo/graphql-schema-types';
import { DocumentNode, Kind, OperationDefinitionNode } from 'graphql';
import { Client, SubscribePayload } from 'graphql-ws';
import { sha256 } from 'js-sha256';
import { Platform } from 'react-native';
import { getUserAgent } from 'react-native-device-info';
import { FetchHomeTilesV2 } from 'services/graphql/queries/FetchHomeTilesV2';
import UrqlSelectionSetKeyDict from 'services/UrqlClient/UrqlSelectionSetKeyDict';
import homeTileTypes from 'shared/constants/homeTileTypes';
import { getPlatformHeaderValue } from 'shared/constants/platform';
import { displayVersion } from 'shared/utils/appVersionUtils';
import { handleGraphQLError } from 'shared/utils/errors';
import getEnvironmentVar from 'shared/utils/getEnvironmentVar';
import {
  CombinedError,
  fetchExchange,
  mapExchange,
  Operation,
  subscriptionExchange,
} from 'urql';

import { mockExchange } from './exchanges/mockExchange';
import { getSchema } from './helpers/urqlClient';

const getAuthExchangeConfig = (
  handleLogout: () => Promise<void>,
  wsClient: Client,
  idToken: string,
  varoSessionId: string,
) => {
  const authExchangeConfig = async (utils: AuthUtilities) => {
    const userAgent = (await getUserAgent()) || '';

    return {
      addAuthToOperation(operation: Operation): Operation {
        const operationName =
          (
            (operation.query as DocumentNode).definitions.filter(
              (definition) =>
                definition.kind === Kind.OPERATION_DEFINITION &&
                definition.name,
            )?.[0] as OperationDefinitionNode
          )?.name?.value ?? '';

        operation.context.url = `${operation.context.url}/${operationName}`;

        if (!idToken) {
          return operation;
        }

        const timezoneOffset = new Date().getTimezoneOffset().toString();

        const generateClientName = () => {
          switch (Platform.OS) {
            case 'ios':
              return '@varo/bank-ios';
            case 'android':
              return '@varo/bank-android';
            default:
              return '@varo/bank-web';
          }
        };

        return utils.appendHeaders(operation, {
          Authorization: `Bearer ${idToken}`,
          'x-varo-tz-offset': timezoneOffset,
          'X-Varo-Platform': getPlatformHeaderValue(),
          'x-varo-session': varoSessionId,
          'apollographql-client-version': displayVersion,
          'apollographql-client-name': generateClientName(),
          'User-Agent': userAgent,
        });
      },
      didAuthError(error: CombinedError) {
        // response is 'any' in CombinedError so difficult to get around type error

        return [401, 403].includes(error.response?.status as number);
      },
      async refreshAuth() {
        await handleLogout();
        void wsClient.dispose();
      },
    };
  };
  return authExchangeConfig;
};

function getClientConfig(
  gqlUrl: string,
  handleLogout: () => Promise<void>,
  wsClient: Client,
  idToken: string,
  varoSessionId: string,
) {
  const exchanges = [
    devtoolsExchange,
    authExchange(
      getAuthExchangeConfig(handleLogout, wsClient, idToken, varoSessionId),
    ),
  ];

  if (getEnvironmentVar('ENABLE_MOCK_EXCHANGE')) {
    exchanges.push(mockExchange());
  }

  exchanges.push(
    cacheExchange({
      updates: {
        Mutation: {
          dismissBannerMessage: (_result, args, cache) => {
            cache.updateQuery(
              {
                query: FetchHomeTilesV2,
                variables: {
                  tileTypes: homeTileTypes,
                },
              },
              (data: HomeTilesV2 | null) => {
                if (!data || !data.systemBanners) {
                  return data;
                }
                data.systemBanners = data.systemBanners.filter(
                  (banner) => banner.dismissId !== args.input,
                );
                return data;
              },
            );
          },
        },
      },
      keys: UrqlSelectionSetKeyDict,
      /*
       * We are merging both the main and subscription schema, there is a potential for conflicts.
       * We will be following up in this ticket: https://varomoney.atlassian.net/browse/JT-121685
       */
      schema: getIntrospectedSchema(getSchema()),
    }),
    persistedExchange({
      generateHash(query) {
        return Promise.resolve(sha256(query));
      },
    }),
    subscriptionExchange({
      forwardSubscription(operation) {
        return {
          subscribe: (sink) => {
            const dispose = wsClient.subscribe(
              operation as SubscribePayload,
              sink,
            );
            return {
              unsubscribe: dispose,
            };
          },
        };
      },
    }),
    mapExchange({
      onError(error) {
        handleGraphQLError(error);
      },
    }),
    fetchExchange,
  );

  return {
    url: gqlUrl,
    exchanges,
  };
}

export default getClientConfig;
